import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useAsyncEffect, useMemoizedFn } from 'ahooks';
import { Button, Divider, Input, Select, Space, Upload, message } from 'antd';
import { clsx } from 'clsx';
import { connect } from 'dva';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Rnd } from 'react-rnd';
import { useImmer } from 'use-immer';

import { createQiniuTokenApi } from '@service';
import { emptyFun, getRandomKey, loadImage, randomNameUpload } from '@untils/common';
import './index.less';

const { Option } = Select;

// 为 Rnd 容器生成可预测的 DOM id
const getAreaElementId = id => `map-area__rnd-${id}`;

// 使用下一帧执行任务，避免跟当前事件循环互相影响
const runAfterFrame = callback => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => callback());
  } else {
    setTimeout(callback, 0);
  }
};

/**
 * 将子元素滚动到父容器的可视区域内
 *
 * 通过 getBoundingClientRect 计算目标元素与容器的相对位置，再结合容器当前的
 * scrollTop/scrollLeft，动态调整滚动条，确保元素上下左右都在可见范围内
 *
 * - 元素在可视区域上方：向上滚动让它可见
 * - 元素在可视区域下方：向下滚动让它可见
 * - 元素在可视区域左侧：向左滚动让它可见
 * - 元素在可视区域右侧：向右滚动让它可见
 *
 * @param {HTMLElement} element 需要被滚动进视口的子元素
 * @param {HTMLElement} container 带滚动条的父级容器
 */
const scrollElementIntoParentView = (element, container) => {
  if (!element || !container) {
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  const offsetTop = elementRect.top - containerRect.top + container.scrollTop;
  const offsetBottom = elementRect.bottom - containerRect.top + container.scrollTop;
  const offsetLeft = elementRect.left - containerRect.left + container.scrollLeft;
  const offsetRight = elementRect.right - containerRect.left + container.scrollLeft;

  if (offsetTop < container.scrollTop) {
    container.scrollTop = offsetTop;
  } else if (offsetBottom > container.scrollTop + container.clientHeight) {
    container.scrollTop = offsetBottom - container.clientHeight;
  }

  if (offsetLeft < container.scrollLeft) {
    container.scrollLeft = offsetLeft;
  } else if (offsetRight > container.scrollLeft + container.clientWidth) {
    container.scrollLeft = offsetRight - container.clientWidth;
  }
};

const mapStateToProps = state => ({
  user: state.userInfo.user,
});

const UploadButton = connect(mapStateToProps)(({ user, children = '上传图片', onFinish, ...uploadProps }) => {
  const [isUploading, setIsUploading] = useState(false);

  const getData = useMemoizedFn(async file => {
    const res = await createQiniuTokenApi({
      method: 'post',
      token: 'adv',
    });
    const { prefix, token } = res.data;
    const key = prefix + '/' + randomNameUpload(file.name);

    return {
      key,
      token,
      'x:prefix': prefix,
      'x:creater_id': user.id,
    };
  });

  const handleUploadFinish = ({ file }) => {
    if (file.status === 'done') {
      const { domain, key } = file.response;

      onFinish?.([domain, key].join('/'));
    }

    setIsUploading(file.status === 'uploading');
  };

  return (
    <Upload
      action="https://upload.qiniup.com"
      accept="image/*"
      maxCount={10}
      multiple
      showUploadList={false}
      data={getData}
      onChange={handleUploadFinish}
      {...uploadProps}
    >
      <Button icon={<UploadOutlined />} disabled={isUploading} loading={isUploading}>
        {children}
      </Button>
    </Upload>
  );
});

// 显示宽度
const DISPLAY_WIDTH = 375;
// APP上的实际显示宽度
const ACTUAL_WIDTH = 750;
const scale = DISPLAY_WIDTH / ACTUAL_WIDTH;

/**
 * 热区组件
 * @param {object} props
 * @param {1|2} props.type - 类型：1 落地页 2 广告位
 * @param {boolean} props.disabled - 是否禁用编辑
 * @param {1|2} props.channel - 渠道：1 APP 2 C端
 * @param {string} props.html - 初始的热区代码
 * @param {function(string): void} props.onFinish - 生成的热区代码
 * @returns {React.ReactNode}
 */
export default function MapArea({ type: areaType = 1, disabled, channel = 1, html = '', onFinish = emptyFun }) {
  const root = useMemo(() => new DOMParser().parseFromString(html, 'text/html'), [html]);
  const [imageUrl, setImageUrl] = useState('');
  const [imageInfo, setImageInfo] = useState({
    width: 0,
    height: 0,
  });
  const [areas, updateAreas] = useImmer([]);
  const [activeId, setActiveId] = useState('');
  const [copiedAreaId, setCopiedAreaId] = useState('');
  const scrollRef = useRef(null);
  const formScrollRef = useRef(null);
  const groupRefs = useRef({});
  const suppressInputFocusRef = useRef(new Set());

  const handleTypeChange = (index, val) => {
    updateAreas(draft => {
      draft[index].type = val;
    });
  };

  const handleHrefChange = (index, val) => {
    updateAreas(draft => {
      draft[index].href = val;
    });
  };

  // 鼠标按下时只更新激活态，避免重复触发 focus 联动
  const handleAreaMouseDown = id => {
    setActiveId(id);
  };

  const handleAreaClick = id => {
    setActiveId(id);

    const group = groupRefs.current[id];
    const input = group?.querySelector('.map-area__input') || group?.querySelector('input');

    if (input) {
      suppressInputFocusRef.current.add(id); // 标记状态，阻止输入框聚焦时再次触发联动

      try {
        input.focus({ preventScroll: true });
      } catch (e) {
        // 一些浏览器/环境可能不支持 preventScroll 选项
        if (process.env.NODE_ENV !== 'production') {
          console.warn('input.focus({ preventScroll: true }) is not supported; falling back to input.focus():', e);
        }
        input.focus();
      }

      runAfterFrame(() => {
        suppressInputFocusRef.current.delete(id);
        scrollElementIntoParentView(group, formScrollRef.current); // 将对应表单项滚动到可视区域
      });
    } else {
      runAfterFrame(() => {
        scrollElementIntoParentView(group, formScrollRef.current);
      });
    }
  };

  const handleDeleteClick = index => {
    if (areas[index].id === activeId) {
      setActiveId('');
    }

    updateAreas(draft => {
      draft.splice(index, 1);
    });
  };

  const handleAddClick = copiedAreaId => {
    const id = getRandomKey();
    const type = areaType === 1 ? 'goods' : 'url';
    const fromId = copiedAreaId || activeId;

    // 1. 如果当前有选中的热区，则在右下方直接添加相同大小的新热区
    // 2. 如果当前没有选中的热区，则在当前视图中央添加 150*50 的新热区
    if (fromId) {
      const activeArea = areas.find(area => area.id === fromId);

      updateAreas(draft => {
        draft.push({
          id,
          type,
          href: '',
          x: activeArea.x + 15,
          y: activeArea.y + 15,
          width: activeArea.width,
          height: activeArea.height,
        });
      });
    } else {
      const defaultWidth = 150;
      const defaultHeight = 50;
      const { scrollTop, clientHeight } = scrollRef.current;

      updateAreas(draft => {
        draft.push({
          id,
          type,
          href: '',
          x: (DISPLAY_WIDTH - defaultWidth) / 2,
          y: scrollTop + (clientHeight - defaultHeight) / 2,
          width: defaultWidth,
          height: defaultHeight,
        });
      });
    }

    handleAreaClick(id);
  };

  const handleDragStop = (index, [x, y]) => {
    updateAreas(draft => {
      draft[index].x = x;
      draft[index].y = y;
    });
  };

  const handleResizeStop = (index, [x, y, width, height]) => {
    updateAreas(draft => {
      draft[index].x = x;
      draft[index].y = y;
      draft[index].width = width;
      draft[index].height = height;
    });
  };

  const handleSubmit = () => {
    if (!imageUrl) {
      message.warn(`请先上传一张${areaType === 1 ? '活动落地页' : '广告位'}图片`);
      return;
    }

    if (areas.length === 0) {
      message.warn('请至少添加一个热区');
      return;
    }

    const $container = document.createElement('div');
    const $image = document.createElement('img');
    const $map = document.createElement('map');
    const mapName = 'map' + Date.now();

    $container.id = 'codeArea';
    $container.className = 'codeArea';
    $container.style.width = ACTUAL_WIDTH + 'px';
    $container.style.height = Math.round((imageInfo.height / imageInfo.width) * ACTUAL_WIDTH) + 'px';
    $container.style.overflow = 'hidden';

    $image.src = imageUrl;
    $image.useMap = '#' + mapName;
    $image.style.display = 'block';
    $image.style.width = '100%';

    $map.id = 'map';
    $map.name = mapName;

    for (let i = 0; i < areas.length; i++) {
      const { x, y, width, height, href, type } = areas[i];
      const $area = document.createElement('area');

      if (!href) {
        message.warn('请完整填写热区内容');
        return;
      }

      if (type === 'goods') {
        if (isNaN(href)) {
          message.warn('请填写纯数字的商品ID');
          return;
        }
        $area.href = `appinternalscheme://goodDetail&goodsid=${href}`;
      } else {
        const isHttps = /^https:\/\//.test(href);
        const isHttp = /^http:\/\//.test(href);

        if (process.env.ALLOW_HTTP_URL === '1') {
          // 允许HTTP时，检查是否为http或https开头
          if (!isHttps && !isHttp) {
            message.warn('请填写以 http:// 或 https:// 开头的 URL');
            return;
          }
        } else if (!isHttps) {
          // 不允许HTTP时，只检查是否为https开头
          message.warn('请填写 https:// 开头的 URL');
          return;
        }

        // 1|| + URL 表示打开带导航栏的H5
        // 2|| + URL 表示打开不带导航栏的H5
        // 3|| + 商品ID 表示跳转普通商品
        // 4|| + 商品ID 表示跳转秒杀商品
        // 5|| + 分类ID 表示跳转分类列表
        //
        // C端热区不需要拼前缀
        $area.href = channel === 2 ? href : '1||' + href;
      }

      $area.coords = [x, y, x + width, y + height].map(n => Math.floor(n / scale)).join(',');
      $area.shape = 'rect';
      $area.target = '_blank';
      $area.style.outline = 'none';

      $map.appendChild($area);
    }

    $container.appendChild($image);
    $container.appendChild($map);

    onFinish($container.outerHTML);
  };

  const handleInputFocus = id => {
    if (suppressInputFocusRef.current.has(id)) {
      return;
    }

    setActiveId(id);

    runAfterFrame(() => {
      const areaElement = document.getElementById(getAreaElementId(id));

      scrollElementIntoParentView(areaElement, scrollRef.current); // 输入聚焦时同步左侧热区
    });
  };

  const handleInputBlur = id => {
    if (suppressInputFocusRef.current.has(id)) {
      return;
    }

    setActiveId(prev => (prev === id ? '' : prev));
  };

  useEffect(() => {
    setImageUrl(root.querySelector('img')?.getAttribute('src'));
  }, [root]);

  useEffect(() => {
    // 需要延迟等待 Rnd 的父组件渲染，否则首次渲染的位置计算有问题
    // https://github.com/bokuweb/react-rnd/issues/846
    setTimeout(() => {
      const el = root.getElementById('codeArea');
      const currentScale = el ? DISPLAY_WIDTH / Number(el.style.width.replace('px', '')) : scale;

      updateAreas(
        Array.from(root.querySelectorAll('area'))
          .filter(area => !area.getAttribute('href').includes('yymoban'))
          .map(area => {
            const originHref = area.getAttribute('href');
            const type = /^(appinternalscheme|yizhen)/.test(originHref) ? 'goods' : 'url';
            const href =
              type === 'goods'
                ? originHref.split('=')[1]
                : /^\d\|\|/.test(originHref)
                  ? originHref.split('||')[1]
                  : originHref;
            const [x1, y1, x2, y2] = area
              .getAttribute('coords')
              .split(',')
              .map(n => Number(n) * currentScale);

            return {
              id: getRandomKey(),
              type,
              href,
              x: x1,
              y: y1,
              width: x2 - x1,
              height: y2 - y1,
            };
          })
      );
    }, 500);
  }, [root]);

  useAsyncEffect(async () => {
    if (imageUrl) {
      try {
        const image = await loadImage(imageUrl);

        setImageInfo({
          width: image.width,
          height: image.height,
        });
      } catch {
        message.error('图片加载失败，请重试');
      }
    }
  }, [imageUrl]);

  // 复制粘贴选区快捷键
  useHotkeys(
    'mod+shift+c',
    () => {
      if (activeId) {
        setCopiedAreaId(activeId);
      }
    },
    { enableOnFormTags: ['input'], preventDefault: true },
    [activeId]
  );
  useHotkeys(
    'mod+shift+v',
    () => {
      if (copiedAreaId) {
        handleAddClick(copiedAreaId);
      }
    },
    { enableOnFormTags: ['input'], preventDefault: true },
    [copiedAreaId]
  );

  return (
    <div className="map-area">
      <div className="map-area__main">
        <div className="map-area__left">
          <Space className="map-area__title">
            <span>一、上传一张{areaType === 1 ? '活动落地页' : '广告位'}图片（建议宽度750px）</span>
            {!disabled && <UploadButton onFinish={setImageUrl}>{imageUrl ? '替换图片' : '上传图片'}</UploadButton>}
          </Space>
          {imageUrl && (
            <div ref={scrollRef} className="map-area__scrollable">
              <div className="map-area__container">
                <img className="map-area__image" width={DISPLAY_WIDTH} src={imageUrl} />
                {areas.map((area, index) => (
                  <Rnd
                    key={area.id}
                    id={getAreaElementId(area.id)}
                    className={clsx('map-area__rnd', area.id === activeId && 'map-area__rnd--active')}
                    bounds="parent"
                    minWidth={50}
                    minHeight={50}
                    resizeHandleClasses={{
                      bottom: 'map-area__handle map-area__handle--b',
                      bottomLeft: 'map-area__handle map-area__handle--bl',
                      bottomRight: 'map-area__handle map-area__handle--br',
                      left: 'map-area__handle map-area__handle--l',
                      right: 'map-area__handle map-area__handle--r',
                      top: 'map-area__handle map-area__handle--t',
                      topLeft: 'map-area__handle map-area__handle--tl',
                      topRight: 'map-area__handle map-area__handle--tr',
                    }}
                    disableDragging={disabled}
                    position={{ x: area.x, y: area.y }}
                    size={{ width: area.width, height: area.height }}
                    onDragStop={(e, data) => handleDragStop(index, [data.x, data.y])}
                    onResizeStop={(e, direction, ref, delta, position) =>
                      handleResizeStop(index, [position.x, position.y, ref.offsetWidth, ref.offsetHeight])
                    }
                    onClick={() => handleAreaClick(area.id)}
                    onMouseDown={() => handleAreaMouseDown(area.id)} // 先记录选中，拖拽时也能保持选中态
                  >
                    {index + 1}
                  </Rnd>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="map-area__right">
          <div className="map-area__title">二、划出热区，设置{areaType === 1 ? '跳转的商品ID' : '跳转地址'}</div>
          {imageUrl ? (
            <div ref={formScrollRef} className="map-area__scrollable map-area__form">
              {areas.map((area, index) => (
                <div
                  key={area.id}
                  className="map-area__group"
                  ref={el => {
                    if (el) {
                      groupRefs.current[area.id] = el;
                    } else {
                      delete groupRefs.current[area.id];
                    }
                  }}
                >
                  <div className={clsx('map-area__index', area.id === activeId && 'map-area__index--active')}>
                    {index + 1}.
                  </div>
                  <Input.Group compact>
                    <Select
                      className="map-area__select"
                      disabled={disabled}
                      value={area.type}
                      onChange={val => handleTypeChange(index, val)}
                    >
                      {areaType === 1 && <Option value="goods">商品ID</Option>}
                      <Option value="url">URL</Option>
                    </Select>
                    <Input
                      id={`map-area__input-${area.id}`}
                      className="map-area__input"
                      placeholder={area.type === 'url' ? '请输入跳转的URL' : '请输入商品SKU平台ID'}
                      readOnly={disabled}
                      value={area.href}
                      onChange={e => handleHrefChange(index, e.target.value)}
                      onFocus={() => handleInputFocus(area.id)}
                      onBlur={() => handleInputBlur(area.id)}
                    />
                    <Button
                      className="map-area__delete"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={disabled}
                      onClick={() => handleDeleteClick(index)}
                    />
                  </Input.Group>
                </div>
              ))}

              {!disabled && (
                <div className="map-area__add">
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => handleAddClick()}>
                    添加热区
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div>请先上传图片</div>
          )}
        </div>
      </div>

      <Divider />

      {!disabled && (
        <Button type="primary" onClick={handleSubmit}>
          提交
        </Button>
      )}
    </div>
  );
}
